import { More } from '@ankr.com/ui';
import { IconButton, Menu, Fade } from '@mui/material';
import { Chain } from '@ankr.com/chains-list';

import { useHasCodeExample } from 'modules/chains/hooks/useHasCodeExample';
import { useMenu } from 'modules/common/hooks/useMenu';

import {
  AddToProjectMenuItem,
  useHasAddToProjectItem,
} from './components/AddToProjectMenuItem';
import {
  AddToWalletMenuItem,
  useHasAddToWalletItem,
} from './components/AddToWalletMenuItem';
import { ChainSelectorsProvider } from '../ChainSelectorsProvider';
import { CodeExampleMenuItem } from './components/CodeExampleMenuItem';
import { DocsMenuItem, useChainDocsLink } from './components/DocsMenuItem';
import { useChainMenuStyles } from './useChainMenuStyles';

interface ChainMenuProps {
  chain: Chain;
  hasAddToProjectItem?: boolean;
}

export const ChainMenu = ({
  chain,
  hasAddToProjectItem: hasAddToProjectItemExternal = true,
}: ChainMenuProps) => {
  const { classes } = useChainMenuStyles();

  const { anchorEl, handleClose, handleOpen, open } = useMenu();

  const hasAddToWalletItem = useHasAddToWalletItem(chain);
  const hasAddToProjectItem =
    useHasAddToProjectItem() && hasAddToProjectItemExternal;
  const hasCodeExampleItem = useHasCodeExample(chain);
  const docsLink = useChainDocsLink(chain);

  const allItemsHidden =
    !hasAddToWalletItem &&
    !hasAddToProjectItem &&
    !hasCodeExampleItem &&
    !docsLink;

  if (allItemsHidden) {
    return null;
  }

  return (
    <div className={classes.root}>
      <IconButton onClick={handleOpen} color="secondary">
        <More />
      </IconButton>
      <Menu
        TransitionComponent={Fade}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        disableScrollLock
        keepMounted
        onClose={() => handleClose()}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ChainSelectorsProvider chain={chain}>
          {hasAddToWalletItem && (
            <AddToWalletMenuItem chain={chain} closeMenu={handleClose} />
          )}
          {hasAddToProjectItem && (
            <AddToProjectMenuItem chain={chain} closeMenu={handleClose} />
          )}
          {hasCodeExampleItem && (
            <CodeExampleMenuItem chain={chain} closeMenu={handleClose} />
          )}
          {docsLink && <DocsMenuItem closeMenu={handleClose} link={docsLink} />}
        </ChainSelectorsProvider>
      </Menu>
    </div>
  );
};
